import React from "react";
import styled from "styled-components";

import { Fonts } from "assets/fonts";
import { CrossIcon } from "assets/icons";
import { viewPortBreakpoints } from "ui/styles/viewport";

import { EditAction } from "./EditAction";
import { MoreActions } from "./MoreActions";
import { ActionButton } from "./styled";

const Container = styled.article<{ $isVisible: boolean }>`
  display: ${(p) => (p.$isVisible ? "flex" : "none")};
  flex-direction: column;
  visibility: ${(p) => (p.$isVisible ? "visible" : "hidden")};
  height: 100%;
  background: ${(p) => p.theme.bgHighestContrast};
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  padding: 12px 16px;
  border-bottom: 1px solid ${(p) => p.theme.borderDefault};

  @media (min-width: ${viewPortBreakpoints.xl}) {
    padding: 16px 24px;
  }
`;

const CloseActionButton = styled(ActionButton)`
  position: relative;
  margin-left: 12px;

  &::before {
    position: absolute;
    left: -12px;
    display: block;
    content: "";
    width: 1px;
    height: 100%;
    background-color: ${(p) => p.theme.borderDefault};
  }

  svg {
    width: 14px;
  }
`;

const Title = styled.h2`
  flex: 1;
  margin: 0;
  ${Fonts.SEMIBOLD_12}
  color: ${(p) => p.theme.textMediumContrast};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 100%;
`;

const Content = styled.section`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
  gap: 32px;
  padding: 16px;

  @media (min-width: ${viewPortBreakpoints.xl}) {
    padding: 32px 24px;
  }
`;

const Footer = styled.footer`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  align-items: center;
  padding: 12px 16px;
  border-top: 1px solid ${(p) => p.theme.borderDefault};

  @media (min-width: ${viewPortBreakpoints.xl}) {
    padding: 16px 24px;
  }
`;

interface SidePanelViewTemplateProps {
  children?: React.ReactNode;
  className?: string;
  displayFooter?: boolean;
  id?: string;
  isVisible?: boolean;
  title: string;
  onClose: () => void;
  renderActions?: () => React.ReactNode;
  renderFooter?: () => React.ReactNode;
}
const SidePanelViewTemplate = (props: SidePanelViewTemplateProps) => {
  const {
    children,
    className,
    displayFooter = false,
    id,
    title,
    isVisible,
    onClose,
    renderActions,
    renderFooter,
  } = props;
  const cn = className ? `${className}-` : "";

  return (
    <Container id={id} className={className} $isVisible={isVisible ?? true}>
      <Header className={`${cn}header`}>
        <Title>{title}</Title>
        <Actions>
          {renderActions && renderActions()}
          <CloseActionButton aria-label="Collapse" title="Collapse" onClick={onClose}>
            <CrossIcon />
          </CloseActionButton>
        </Actions>
      </Header>
      <Content className={`${cn}content`}>{children}</Content>
      {displayFooter && !!renderFooter && <Footer>{renderFooter()}</Footer>}
    </Container>
  );
};

SidePanelViewTemplate.EditAction = EditAction;
SidePanelViewTemplate.MoreActions = MoreActions;

export { SidePanelViewTemplate };
